/** @jsxImportSource @emotion/react */
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Form, Modal } from "antd";
import { AuthApi } from "api/auth";
import { AffiliateRegisterFormType } from "api/auth/type";
import InputCustom from "components/base/InputCustom";
import InputPassword from "components/base/InputPassword";
import InputPhoneNumber from "components/base/InputPhoneNumber";
import {
  REGEX_EMAIL,
  REGEX_PASSWORD,
  REGEX_USERNAME_AGENT
} from "constants/regex";
import { ResponseCode } from "constants/response";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { selectIsLoading } from "store/auth";
import { setOpenModal } from "store/modal";
import { selectProfile } from "store/user";
import { outletContextType } from "types";
import { ModalTypeValue } from "types/modal";
import * as yup from "yup";

import WrapperForm from "../WrapperForm";
import { RegisterModalStyle } from "./index.style";

function RegisterAffiliateModal() {
  const { openNotification } = useOutletContext<outletContextType>();
  const profile = useAppSelector(selectProfile);

  const { t } = useTranslation([
    "common",
    "register",
    "validate",
    "placeholder"
  ]);

  const schema = yup
    .object({
      username: yup
        .string()
        .trim()
        .required(t("validate:FieldRequired"))
        .matches(REGEX_USERNAME_AGENT, t("validate:UsernameValid")),
      password: yup
        .string()
        .trim()
        .required(t("validate:FieldRequired"))
        .matches(REGEX_PASSWORD, t("validate:PasswordValid")),
      fullname: yup.string().trim().required(t("validate:FieldRequired")),
      mobile: yup
        .string()
        .trim()
        .required(t("validate:FieldRequired"))
        .min(10, t("validate:Min"))
        .max(14, t("validate:Max")),
      email: yup
        .string()
        .required(t("validate:FieldRequired"))
        .matches(REGEX_EMAIL, t("validate:EmailRequired")),
      whatsapp: yup
        .string()
        .trim()
        .required(t("validate:FieldRequired"))
        .max(14, t("validate:Max"))
    })
    .required();

  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectIsLoading);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid }
  } = useForm<AffiliateRegisterFormType>({
    resolver: yupResolver<any>(schema),
    mode: "all"
  });

  // const areaCodeValidate = (number: string) => {
  //   if (number.startsWith("+")) {
  //     number = number.slice(1);
  //   }
  //   if (number.startsWith("0")) {
  //     return "880" + number.slice(1);
  //   }

  //   if (number.slice(0, 3) !== "880") {
  //     return "880" + number;
  //   }
  //   return number;
  // };

  const onSubmit = async (data: AffiliateRegisterFormType) => {
    const dataForm = {
      ...data,
      player_id: profile?.player_id ?? null,
      mobile: data.mobile.slice(1)
    };

    const res = await AuthApi.affiliateRegister(dataForm);

    const resData = res.data;

    if (resData.status !== ResponseCode.SUCCESS) {
      openNotification("error", resData.msg as string);
      return;
    }
    openNotification("success", resData.msg as string);
    dispatch(setOpenModal(ModalTypeValue.Close));
  };

  return (
    <Modal
      open={true}
      closable={false}
      onCancel={() => dispatch(setOpenModal(ModalTypeValue.Close))}
      css={RegisterModalStyle.modalContainer}
      footer={<></>}
      centered
    >
      <WrapperForm
        title={t("register:title")}
        formType={ModalTypeValue.Register}
        body={
          <div css={RegisterModalStyle.formContainer}>
            <Form
              css={RegisterModalStyle.formContainer}
              onSubmitCapture={handleSubmit(onSubmit)}
              autoComplete="off"
            >
              <div css={RegisterModalStyle.inputControl}>
                <label>{t("placeholder:AgentName")}</label>
                <InputCustom
                  disabled={isLoading}
                  placeholder={t("placeholder:AgentName")}
                  name={"username"}
                  control={control}
                  errors={errors.username?.message}
                  margin_bottom={"8px"}
                  validate_status={errors.username ? "error" : "success"}
                  maxLength={16}
                  autoComplete="off"
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>{t("placeholder:Password")}</label>
                <InputPassword
                  disabled={isLoading}
                  placeholder={t("placeholder:Password")}
                  name={"password"}
                  control={control}
                  margin_bottom="8px"
                  error={errors.password?.message}
                  validate_status={errors.password ? "error" : "success"}
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>{t("placeholder:FullName")}</label>
                <InputCustom
                  placeholder={t("placeholder:FullName")}
                  name={"fullname"}
                  control={control}
                  margin_bottom={"8px"}
                  disabled={isLoading}
                  errors={errors.fullname?.message}
                  validate_status={errors.fullname ? "error" : "success"}
                  onKeyDown={(e) => {
                    const key = e.key;

                    const regex = new RegExp("^[a-zA-Z\\s]+$");

                    if (!regex.test(key) && key !== "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              {/* <div css={RegisterModalStyle.inputControl}>
                <label>{t("register:email_label")}</label>
                <InputCustom
                  disabled={isLoading}
                  placeholder={t("register:email_placeholder")}
                  name={"email"}
                  margin_bottom="0px"
                  control={control}
                  errors={errors.email?.message}
                  validate_status={errors.email ? "error" : "success"}
                />
              </div> */}
              <div css={RegisterModalStyle.inputControl}>
                <label>{t("placeholder:PhoneNumber")}</label>
                <InputPhoneNumber
                  disabled={isLoading}
                  name={"mobile"}
                  control={control}
                  margin_bottom="0px"
                  error={errors.mobile?.message}
                  validate_status={errors.mobile ? "error" : "success"}
                  placeholder={t("placeholder:PhoneNumber")}
                  onChange={(val) => {
                    //
                  }}
                />
              </div>
              {/* <div css={RegisterModalStyle.inputControl}>
                <label>{t("register:FullName")}</label>
                <InputCustom
                  disabled={isLoading}
                  placeholder={t("register:FullName")}
                  name={"fullname"}
                  margin_bottom="0px"
                  control={control}
                  errors={errors.fullname?.message}
                  validate_status={errors.fullname ? "error" : "success"}
                />
              </div> */}
              <div css={RegisterModalStyle.inputControl}>
                <label>{t("placeholder:Email")}</label>
                <InputCustom
                  placeholder={t("placeholder:Email")}
                  name={"email"}
                  control={control}
                  margin_bottom={"8px"}
                  errors={errors.email?.message}
                  validate_status={errors.email ? "error" : "success"}
                  disabled={isLoading}
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>{t("placeholder:WhatsAppLink")}</label>
                <InputCustom
                  placeholder={t("placeholder:WhatsAppLink")}
                  name={"whatsapp"}
                  type="number"
                  control={control}
                  margin_bottom={"8px"}
                  errors={errors.whatsapp?.message}
                  validate_status={errors.whatsapp ? "error" : "success"}
                  disabled={isLoading}
                />
              </div>

              <Button
                disabled={!isValid}
                loading={isLoading}
                css={[
                  RegisterModalStyle.buttonSubmit,
                  isValid && RegisterModalStyle.buttonActive
                ]}
                htmlType="submit"
              >
                {t("register:submit_button")}
              </Button>
            </Form>
          </div>
        }
        // footer={
        //   <div css={RegisterModalStyle.footer}>
        //     <p>
        //       {t("register:already_account")}{" "}
        //       <span
        //         onClick={() => dispatch(setOpenModal(ModalTypeValue.Login))}
        //       >
        //         {t("register:go_to_login")}
        //       </span>
        //     </p>
        //   </div>
        // }
      />
    </Modal>
  );
}

export default RegisterAffiliateModal;
